import React, { useState, useEffect } from 'react';
import { SupabaseAppProvider, useApp } from './context/SupabaseAppContext';
import { testConnection } from './lib/supabase';
import { validateAuthState } from './lib/authRouter';
import Header from './components/Layout/Header';
import Dashboard from './components/Dashboard/Dashboard';
import IdeaForm from './components/Ideas/IdeaForm';
import IdeasManagement from './components/Ideas/IdeasManagement';
import TaskForm from './components/Tasks/TaskForm';
import TasksManagement from './components/Tasks/TasksManagement';
import Roadmap from './components/Roadmap/Roadmap';
import SearchPage from './components/Search/SearchPage';
import AuthScreen from './components/Auth/AuthScreen';
import FirstTimeSetup from './components/Auth/FirstTimeSetup';
import ProfileForm from './components/Profile/ProfileForm';
import { Idea, Task } from './types';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { getURLState, clearURLState } from './lib/urlState';
import { clearAllSessions } from './lib/sessionPersistence';

// Using native Supabase Azure OAuth - no special callback handling needed

const AppContent: React.FC = () => {
  // Get current page from URL hash or default to dashboard
  const getCurrentPageFromURL = () => {
    const hash = window.location.hash.replace('#', '');
    const validPages = ['dashboard', 'ideas', 'roadmap', 'tasks', 'search', 'notifications', 'settings', 'profile'];
    return validPages.includes(hash) ? hash : 'dashboard';
  };

  const [currentPage, setCurrentPage] = useState(getCurrentPageFromURL());
  const [showIdeaForm, setShowIdeaForm] = useState(false);
  const [editingIdea, setEditingIdea] = useState<Idea | null>(null);
  const [showTaskForm, setShowTaskForm] = useState(false);
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [showProfileForm, setShowProfileForm] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<'checking' | 'connected' | 'failed'>('checking');
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const { state } = useApp();

  // Handle page changes and update URL
  const handlePageChange = (page: string) => {
    setCurrentPage(page);
    window.location.hash = page;
    
    // Clear URL state when navigating away from Ideas management
    const urlState = getURLState();
    if (page !== 'ideas' && urlState.selectedIdeaId) {
      clearURLState(['selectedIdeaId', 'analysisState', 'analysisType']);
    }
  };

  // Listen for URL hash changes (back/forward buttons)
  useEffect(() => {
    const handleHashChange = () => {
      const newPage = getCurrentPageFromURL();
      setCurrentPage(newPage);
      
      // Clear URL state when navigating away from Ideas management
      const urlState = getURLState();
      if (newPage !== 'ideas' && urlState.selectedIdeaId) {
        clearURLState(['selectedIdeaId', 'analysisState', 'analysisType']);
      }
    };
    
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  // Handle editing an idea
  const handleEditIdea = (idea: Idea) => {
    setEditingIdea(idea);
    setShowIdeaForm(true);
  };

  // Handle closing the idea form
  const handleCloseIdeaForm = () => {
    setShowIdeaForm(false);
    const wasEditing = editingIdea;
    setEditingIdea(null);
    
    // If we were editing an idea and have URL state, restore the ideas page with selection
    if (wasEditing && currentPage === 'ideas') {
      const urlState = getURLState();
      if (urlState.selectedIdeaId === wasEditing.id) {
        // The IdeasManagement component will handle restoring the selection from URL
      }
    }
  };

  // Handle editing a task
  const handleEditTask = (task: Task) => {
    setEditingTask(task);
    setShowTaskForm(true);
  };

  // Handle closing the task form
  const handleCloseTaskForm = () => {
    setShowTaskForm(false);
    setEditingTask(null);
  };
  
  // Test connection to Supabase (silent background check)
  useEffect(() => {
    const checkConnection = async () => {
      try {
        const result = await testConnection();
        
        if (result.ok) {
          setConnectionStatus('connected');
          setConnectionError(null);
        } else {
          setConnectionStatus('failed');
          setConnectionError(result.error || 'Could not connect to database');
        }
      } catch (error: any) {
        setConnectionStatus('failed');
        setConnectionError(error.message || 'Connection test failed');
      }
    };
    
    // Set connected immediately for better UX, but still test in background
    setConnectionStatus('connected');
    checkConnection();
  }, []);

  // Handle authentication callbacks (WorkOS, Supabase magic links, OAuth)
  useEffect(() => {
    // Only proceed if we have a good connection
    if (connectionStatus !== 'connected') return;
    
    const url = new URL(window.location.href);
    
    // Handle /auth/callback route by redirecting to main page with params
    if (url.pathname === '/auth/callback') {
      console.log('🔄 Detected /auth/callback route, processing...');
      // Keep the search params but change path to root
      const newUrl = `${url.origin}${url.search}`;
      window.history.replaceState({}, document.title, newUrl);
      // The rest of the callback logic will handle the parameters
    }
    
    // Check for OAuth errors first
    if (url.searchParams.has('error')) {
      const error = url.searchParams.get('error');
      const errorDescription = url.searchParams.get('error_description');
      console.error('OAuth Error:', error, errorDescription);
      
      // Clear the error from URL and show user-friendly message
      window.history.replaceState({}, document.title, window.location.origin);
      
      // Set error state for user to see
      setConnectionError(`Authentication failed: ${errorDescription || error}`);
      
      return;
    }
    
    // Native Supabase Azure OAuth handles callbacks automatically
    
    // Handle regular OAuth errors (non-WorkOS)
    if (url.searchParams.has('error')) {
      console.log('🔍 URL params check:', {
        hasCode: url.searchParams.has('code'),
        hasState: url.searchParams.has('state'),
        allParams: Object.fromEntries(url.searchParams.entries())
      });
    }
    
    // Handle other OAuth callbacks (Azure, etc.)
    if (url.searchParams.has('code') || url.hash.includes('access_token')) {
      // Clear auth params from URL without reloading
      window.history.replaceState({}, document.title, window.location.origin);
    }
  }, [connectionStatus]);


  // Clean up sessions when the app is about to unload
  useEffect(() => {
    const handleBeforeUnload = () => {
      clearAllSessions();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  const renderCurrentPage = () => {
    switch (currentPage) {
             case 'dashboard':
         return <Dashboard onPageChange={handlePageChange} onShowIdeaForm={() => {
           setEditingIdea(null); // Clear any existing editing state
           setShowIdeaForm(true);
         }} />;
      case 'ideas':
        return <IdeasManagement 
          onShowIdeaForm={() => {
            setEditingIdea(null); // Clear any existing editing state
            setShowIdeaForm(true);
          }} 
          onEditIdea={handleEditIdea}
        />;
      case 'roadmap':
        return <Roadmap onPageChange={handlePageChange} onEditIdea={handleEditIdea} />;
      case 'tasks':
        return <TasksManagement 
          onShowTaskForm={() => setShowTaskForm(true)} 
          onEditTask={handleEditTask}
        />;
      case 'search':
        return <SearchPage onPageChange={handlePageChange} onEditIdea={handleEditIdea} onEditTask={handleEditTask} />;
      case 'notifications':
        return (
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="mb-8">
              <h1 className="text-3xl font-bold text-gray-900">Notifications</h1>
              <p className="text-gray-600 mt-2">
                Stay updated with the latest activity
              </p>
            </div>
            <div className="card">
              <div className="card-body">
                <p className="text-center text-gray-500 py-12">
                  Notifications center coming soon...
                </p>
              </div>
            </div>
          </div>
        );
      case 'settings':
        return (
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="mb-8">
              <h1 className="text-3xl font-bold text-gray-900">Settings</h1>
              <p className="text-gray-600 mt-2">
                Customize your Idea Hopper experience
              </p>
            </div>
            <div className="card">
              <div className="card-body">
                <p className="text-center text-gray-500 py-12">
                  Settings interface coming soon...
                </p>
              </div>
            </div>
          </div>
        );
      case 'profile':
        setShowProfileForm(true);
        handlePageChange('dashboard'); // Return to dashboard after opening profile form
        return <Dashboard onPageChange={handlePageChange} onShowIdeaForm={() => {
          setEditingIdea(null); // Clear any existing editing state
          setShowIdeaForm(true);
        }} />;
      default:
        return <Dashboard onPageChange={handlePageChange} />;
    }
  };

  // Only show connection failed state for critical errors
  if (connectionStatus === 'failed' && connectionError && connectionError.includes('CRITICAL')) {
    return (
      <div className="min-h-screen bg-ocean-mist flex items-center justify-center">
        <div className="text-center max-w-md">
          <div className="text-red-500 text-6xl mb-4">🔌</div>
          <h2 className="text-2xl font-semibold text-red-700 mb-2">Connection Error</h2>
          <p className="text-gray-600 mb-4">
            Could not connect to the Idea Hopper database.
          </p>
          <p className="text-gray-600 mb-6">Error details: {connectionError}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="btn btn-primary"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // Show loading while auth is initializing or data is loading
  if (!state.authInitialized || state.loading) {
    return (
      <div className="min-h-screen bg-ocean-mist flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-deep-teal mx-auto mb-4"></div>
          <h2 className="text-2xl font-semibold text-dark-emerald mb-2">Loading Idea Hopper</h2>
          <p className="text-gray-600">
            {!state.authInitialized ? 'Starting up...' : 'Getting your ideas ready...'}
          </p>
        </div>
      </div>
    );
  }

  // Show error state
  if (state.error) {
    return (
      <div className="min-h-screen bg-ocean-mist flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-semibold text-red-700 mb-2">Connection Error</h2>
          <p className="text-gray-600 mb-4">{state.error}</p>
          <button 
            onClick={() => window.location.href = '/'} 
            className="btn btn-primary"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // Show login screen if no user (auth is initialized but no user found)
  if (!state.currentUser) {
    return <AuthScreen />;
  }

  // Check if user needs to complete their profile setup
  // This happens when they have an account but no proper name set
  const needsSetup = state.currentUser && (
    !state.currentUser.name || 
    state.currentUser.name === state.currentUser.email.split('@')[0] ||
    state.currentUser.name === 'User' ||
    state.currentUser.name.includes('@') // If name still contains email
  );

  // TEMPORARY DEBUG: Skip FirstTimeSetup during testing
  const DEBUG_BYPASS_PROFILE_QUERIES = false; // This should match the flag in SupabaseAppContext
  const shouldShowSetup = needsSetup && !DEBUG_BYPASS_PROFILE_QUERIES;

  if (shouldShowSetup) {
    return <FirstTimeSetup userEmail={state.currentUser.email} />;
  }

  return (
    <div className="min-h-screen bg-ocean-mist">
      <Header currentPage={currentPage} onPageChange={handlePageChange} />
      <main className="pb-8">
        {renderCurrentPage()}
      </main>
      
      {/* Idea Form Modal */}
      <IdeaForm 
        isOpen={showIdeaForm} 
        onClose={handleCloseIdeaForm}
        editingIdea={editingIdea}
      />
      
      {/* Task Form Modal */}
      <TaskForm 
        isOpen={showTaskForm} 
        onClose={handleCloseTaskForm}
        editingTask={editingTask}
      />
      
      {/* Profile Form Modal */}
      <ProfileForm 
        isOpen={showProfileForm} 
        onClose={() => setShowProfileForm(false)} 
      />
      
      {/* Toast Notifications */}
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </div>
  );
};

const App: React.FC = () => {
  return (
    <SupabaseAppProvider>
      <AppContent />
    </SupabaseAppProvider>
  );
};

export default App;
